import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import PostShowNotesModal from './PostShowNotesModal';
import { useToast } from '../components/Toast/ToastContext';
import MessageThread from '../components/MessageThread/MessageThread';
import PageLayout from '../components/ui/PageLayout';
import EventAirspaceWidget from './EventAirspaceWidget';
import EventAirspaceModal from './EventAirspaceModal';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import CompletionRing from '../components/CompletionRing';
import TransportChips, { drivesOut } from '../components/TransportChips';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSiteMapShare } from './useSiteMapShare';
import SiteMapLayer from './SiteMapLayer';
import dayjs from 'dayjs';
import { parseDate, formatEventDate, getDisplayStatus } from '../utils/date-utils';
import { bufferedCount } from '../utils/drone-utils';
import useUserStore from '../stores/useUserStore';
import SparkCreativePanel from '../Spark/SparkCreativePanel';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { CATEGORY } from '../components/ui/categoryColors';
import Tooltip from '../components/ui/Tooltip';
import Tabs from '../components/ui/Tabs';
import { EVENT_LOCKS, isFormFieldIncluded, resolveFormValue } from './event-locks';

// Clean up verbose geocoded addresses for display.
// Strips country, county, abbreviates US states, collapses "number, street" comma.
// Display-only — does NOT modify the stored value or the Maps search URL.
const US_STATES = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC',
};

const formatAddress = (raw) => {
  if (!raw || raw.length < 25) return raw;
  let addr = raw
    .replace(/,?\s*United States\s*$/i, '')
    .replace(/,\s*[\w\s]+ County,/g, ',')
    .replace(/,\s*,/g, ',')
    .trim();
  // "4040, Central Florida Parkway" → "4040 Central Florida Parkway"
  addr = addr.replace(/,\s*(\d+),\s+/, ', $1 ');
  // Abbreviate state: ", Florida, 32837" → ", FL 32837"
  for (const [full, abbr] of Object.entries(US_STATES)) {
    addr = addr.replace(new RegExp(`,\\s*${full},\\s*(\\d{5})`, 'i'), `, ${abbr} $1`);
    addr = addr.replace(new RegExp(`,\\s*${full}\\s*$`, 'i'), `, ${abbr}`);
  }
  return addr.replace(/^,\s*/, '').replace(/,\s*$/, '');
};

// Resolve IANA timezone (e.g. "America/New_York") to its abbreviation ("EDT"/"EST")
// based on the event date so DST is correct.
const getTzAbbrev = (tz, dateStr) => {
  try {
    const date = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date);
    return parts.find(p => p.type === 'timeZoneName')?.value || tz;
  } catch {
    return tz;
  }
};

const makeIcon = (color) => new L.DivIcon({
  className: '',
  html: `<svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="${color}"/>
    <circle cx="12" cy="12" r="5" fill="white" fill-opacity="0.9"/>
  </svg>`,
  iconSize: [24, 36],
  iconAnchor: [12, 36],
  popupAnchor: [0, -36],
});

const launchIcon = makeIcon(CATEGORY.blue.mark.hex);
const audienceIcon = makeIcon(CATEGORY.orange.mark.hex);

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, bounds]);
  return null;
};

// Value for the "Site Map" field. Mirrors the Preview field's
// link style for ready state; renders muted text for loading; amber for
// broken/empty. Edit affordance shown alongside view link for editors.
const SiteMapDataValue = ({ share, canEdit, onEditClick }) => {
  if (share.state === 'loading') {
    return <span className="text-on-surface-variant italic">Loading…</span>;
  }
  if (share.state === 'broken') {
    return <span className="text-amber-400">Link broken — update on event</span>;
  }

  const viewHref = share.viewUrl || share.shareUrl;
  const editHref = share.editUrl;

  // Edit button is a plain anchor opening producer in a new tab. Producer
  // sends `X-Frame-Options: deny` so iframe is impossible; the parent's
  // visibilitychange listener refetches when the user comes back.
  const editLink = canEdit && editHref ? (
    <Tooltip label="Edit in producer (opens new tab)">
    <a
      href={editHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onEditClick}
      className="text-on-surface-variant hover:text-white transition-colors duration-150 inline-flex items-center gap-1 text-xs ml-2"
    >
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
      </svg>
      <span>Edit</span>
    </a>
    </Tooltip>
  ) : null;

  if (share.state === 'empty') {
    return (
      <span className="inline-flex items-center">
        {viewHref ? (
          <a href={viewHref} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors duration-150 hover:underline inline-flex items-center gap-1">
            <span>No site map yet</span>
            <ExternalLinkIcon />
          </a>
        ) : (
          <span className="text-amber-400">No site map yet</span>
        )}
        {editLink}
      </span>
    );
  }

  // ready
  const name = share.siteMap?.name || 'site map';
  return (
    <span className="inline-flex items-center">
      {viewHref ? (
        <a href={viewHref} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors duration-150 hover:underline inline-flex items-center gap-1">
          <span>{name}</span>
          <ExternalLinkIcon />
        </a>
      ) : (
        <span>{name}</span>
      )}
      {editLink}
    </span>
  );
};

// Small "open in new tab" glyph — matches the existing 12px arrow used in
// the Preview row's surrounding chrome elsewhere in the file.
const ExternalLinkIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-4.5-6h6m0 0v6m0-6L9.75 14.25" />
  </svg>
);

const STATUS_TAG = {
  pending: { tone: 'neutral' },
  confirmed: { hex: CATEGORY.blue.ink.hex },
  packing: { hex: CATEGORY.purple.ink.hex },
  active: { hex: CATEGORY.green.ink.hex },
  completed: { hex: CATEGORY.emerald.ink.hex },
  cancelled: { hex: CATEGORY.red.ink.hex },
};

/* Reusable card wrapper */
const Card = ({ children, className = '' }) => (
  <Panel padding="md" className={className}>
    {children}
  </Panel>
);

/* Lock-in status indicators.
 *
 * Each `locked_*` column on the event is a canonical boolean that lives in
 * event-locks.js. A green (closed) padlock means "locked in / approved";
 * a yellow (open) padlock means "still mutable, not locked in yet."
 *
 * LOCK_LABELS provides the tooltip copy that users hover to disambiguate
 * which tab / subsection the badge refers to — important on the Event
 * Details card, which spans 3 lock groups.
 */
const LOCK_LABELS = {
  locked_main: 'Main',
  locked_location: 'Location',
  locked_creative: 'Creative',
  locked_contacts: 'Contacts',
  locked_logistics_crew: 'Crew',
  locked_logistics_flights: 'Flights',
  locked_logistics_rental_car: 'Rental Car',
  locked_logistics_box_truck: 'Box Truck',
  locked_logistics_hotel: 'Hotel',
  locked_logistics_packing: 'Packing',
  locked_logistics_transport: 'Transport',
};

const LockBadge = ({ locked, label, tooltipOverride = false }) => (
  <Tooltip label={tooltipOverride ? label : `${label || 'Section'} ${locked ? 'locked in' : 'not locked in'}`}>
  <span
    className={`inline-flex items-center justify-center shrink-0 ${locked ? 'text-green-400' : 'text-yellow-400/80'}`}
    aria-label={tooltipOverride ? label : `${label || 'Section'} ${locked ? 'locked' : 'unlocked'}`}
  >
    {locked ? (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
    ) : (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
      </svg>
    )}
  </span>
  </Tooltip>
);

/**
 * Day coverage for one crew member, derived from event_crew_shifts.
 *
 * event_crew answers WHO and in what role; shifts are the only answer to WHICH
 * DAYS. Showing a bare name here is exactly what let this page silently disagree
 * with /schedule whenever an assignment was partial or had been shifted.
 *
 * Returns null for the ordinary case (a single shift covering the whole show) so
 * the page stays quiet when there is nothing to notice, and speaks up when there
 * is. A shift outside the show span is a travel or load-in day, not an error.
 */
const crewCoverage = (member, event) => {
  const shifts = member.shifts;
  if (!Array.isArray(shifts)) return null; // external crew, or a pre-shifts payload
  if (shifts.length === 0) return { text: 'No days assigned', tone: 'missing' };

  const day = (v) => String(v).slice(0, 10);
  const evStart = day(event.event_date);
  const evEnd = day(event.end_date || event.event_date);
  const spanDays = parseDate(evEnd).diff(parseDate(evStart), 'day') + 1;

  let covered = 0;
  let outside = false;
  for (const s of shifts) {
    covered += parseDate(day(s.end_date)).diff(parseDate(day(s.start_date)), 'day') + 1;
    if (day(s.start_date) < evStart || day(s.end_date) > evEnd) outside = true;
  }

  if (!outside && shifts.length === 1 && covered >= spanDays) return null;

  const text = shifts
    .map(s => {
      const a = formatEventDate(s.start_date, 'MMM D');
      const b = formatEventDate(s.end_date, 'MMM D');
      return a === b ? a : `${a} to ${b}`;
    })
    .join(', ');

  return { text, tone: outside ? 'offsite' : 'partial' };
};

const COVERAGE_TAG = {
  missing: { tone: 'bad' },
  partial: { tone: 'accent' },
  offsite: { hex: CATEGORY.cyan.ink.hex },
};

const DETAIL_TABS = [
  ['main', 'Main'],
  ['location', 'Location'],
  ['creative', 'Creative'],
  ['logistics', 'Logistics'],
  ['contacts', 'Contacts'],
];

/* Empty tab state. Only Creative and Contacts can come up blank — every other
   tab has at least one unconditional card — but a blank panel reads as a broken
   page, so say what belongs here and offer the way to add it. */
const EmptyTab = ({ children, editTo, canEdit }) => (
  <EmptyState
    action={canEdit ? (
      <Link to={editTo} className={buttonClass('quiet', 'sm')}>
        Add these details
      </Link>
    ) : undefined}
  >
    {children}
  </EmptyState>
);

/* Section heading with subtle left accent. Optional `locks` prop renders
 * a SINGLE aggregate LockBadge to the right of the title. When the section
 * spans multiple lock groups (e.g. "Event Details" covers Main + Location +
 * Creative), the badge goes yellow if ANY sub-lock is unlocked and the
 * tooltip lists the unlocked ones; it only goes green when every sub-lock
 * is in. This keeps the header compact while still surfacing which tabs
 * still need attention on hover. */
const SectionHeading = ({ children, locks }) => {
  let badge = null;
  if (locks && locks.length > 0) {
    const unlocked = locks.filter(l => !l.on);
    const allLocked = unlocked.length === 0;
    const labels = locks.map(l => l.label).join(', ');
    const tooltip = allLocked
      ? `${labels} locked in`
      : `${unlocked.map(l => l.label).join(', ')} not locked in`;
    badge = <LockBadge locked={allLocked} label={tooltip} tooltipOverride />;
  }
  return (
    <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-4 flex items-center gap-2">
      <span className="w-1 h-3.5 rounded-full bg-primary/60 inline-block" />
      <span className="flex-1">{children}</span>
      {badge}
    </h3>
  );
};

/* Read-mode field. Borrows EventForm's STRUCTURE, not its input chrome.
 *
 * What matches the editor: the micro-label typography, the label-above-value
 * stacking, the two- and three-up grid pairings, and the card the group sits in.
 * That is what makes browsing and editing feel like one form in two modes.
 *
 * What deliberately does NOT match: the boxed input surface. Drawing a border and
 * an inset background around a read-only value makes it look like a disabled
 * textarea, which reads as "you are not allowed to type here" rather than "this
 * is the value". Values are plain text; the label carries the structure.
 *
 * An empty value still shows a dash instead of collapsing, so "nobody filled this
 * in" is visible at a glance rather than being an absence you have to notice. */
const Field = ({ label, hint, children, multiline, className = '' }) => {
  const empty = children === null || children === undefined || children === '' || children === false;
  return (
    <div className={className}>
      <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1">
        {label}
        {hint && <span className="text-on-surface-variant ml-1 normal-case tracking-normal font-normal">{hint}</span>}
      </label>
      <div className={`text-sm text-on-surface ${multiline ? 'whitespace-pre-wrap leading-relaxed' : 'flex flex-wrap items-center gap-x-1.5'}`}>
        {empty ? <span className="text-on-surface-disabled">--</span> : children}
      </div>
    </div>
  );
};

const ROLE_TAG = {
  pic:            { hex: CATEGORY.amber.ink.hex },
  'pilot in command': { hex: CATEGORY.amber.ink.hex },
  spotter:        { hex: CATEGORY.sky.ink.hex },
  'safety officer': { hex: CATEGORY.red.ink.hex },
  safety:         { hex: CATEGORY.red.ink.hex },
  vo:             { hex: CATEGORY.indigo.ink.hex },
  'visual observer': { hex: CATEGORY.indigo.ink.hex },
  default:        { tone: 'neutral' },
};

const LOGISTICS_FIELDS = [
  { key: 'gear_transportation', label: 'Gear Transport', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>
  )},
  { key: 'flight', label: 'Flight', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" /></svg>
  )},
  { key: 'rental_car', label: 'Rental Car', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>
  )},
  { key: 'box_truck', label: 'Box Truck', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25m-2.25 0V6.375c0-.621-.504-1.125-1.125-1.125H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125" /></svg>
  )},
  { key: 'hotel', label: 'Hotel', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" /></svg>
  )},
  { key: 'packing', label: 'Packing', icon: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6.912a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H15M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859" /></svg>
  )},
];

const EventDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const user = useUserStore((state) => state.user);
  const canEdit = !!(user?.isadmin || user?.can_admin || user?.can_producer);

  // Tour state
  const [tourData, setTourData] = useState(null);

  // Crew state
  const [crew, setCrew] = useState([]);
  // External (non-Firefleet) crew — contractors / client-side helpers
  const [externalCrew, setExternalCrew] = useState([]);

  // Which tab of the read view is showing. Same five keys, same order and same
  // labels as EventForm — browsing and editing are two modes of one object, so
  // the section a producer learned in the editor is where they look here.
  //
  // The tab also lives in the URL as ?tab=, which is what makes Edit and Save
  // land back where you were instead of resetting to Main. history.replace (not
  // push) so tabbing around doesn't fill the back button with steps.
  const initialTab = new URLSearchParams(location.search).get('tab');
  const [activeTab, setActiveTabState] = useState(
    DETAIL_TABS.some(([k]) => k === initialTab) ? initialTab : 'main'
  );
  const setActiveTab = (key) => {
    setActiveTabState(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };
  const [postShowOpen, setPostShowOpen] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const data = await api.get(`/events/${id}`);
        setEvent(data);
      } catch (error) {
        toast.error('Event not found');
        history.push('/events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  // Fetch tour data if event is in a tour
  useEffect(() => {
    if (!event?.tour_id) { setTourData(null); return; }
    api.get(`/tours/${event.tour_id}`)
      .then(data => setTourData(data))
      .catch(() => setTourData(null));
  }, [event?.tour_id]);

  // Fetch crew (internal + external in parallel — they live in the same Card)
  useEffect(() => {
    api.get(`/events/${id}/crew`)
      .then(data => setCrew(data || []))
      .catch(() => setCrew([]));
    api.get(`/events/${id}/external-crew`)
      .then(data => setExternalCrew(data || []))
      .catch(() => setExternalCrew([]));
  }, [id]);

  // Per-tab completion, read straight off the EVENT_LOCKS config the editor uses.
  // Deliberately NOT a second mapping: if a lock moves tabs or gains a field, both
  // surfaces follow in one edit. resolveFormValue takes the server event shape
  // unchanged — the column names and details_json keys are identical to the form's.
  const tabCompletion = useMemo(() => {
    const buckets = { main: [], location: [], creative: [], logistics: [], contacts: [] };
    if (!event) return buckets;
    for (const lock of EVENT_LOCKS) {
      // A future EVENT_LOCKS entry with a tab name not in `buckets` would
      // otherwise throw inside this memo and blank the whole page. Skip it: a
      // missing ring is a cosmetic gap, an exception is a dead event page.
      if (!buckets[lock.tab]) continue;
      const isLocked = !!event[lock.key];
      for (const fieldDesc of lock.fields) {
        if (!isFormFieldIncluded(fieldDesc, event)) continue;
        const value = resolveFormValue(fieldDesc, event, crew.length, event.contacts);
        buckets[lock.tab].push([value, isLocked]);
      }
    }
    const isFilled = (v) => v !== null && v !== undefined
      && (Array.isArray(v) ? v.length > 0 : typeof v === 'string' ? v.trim().length > 0 : true);
    const out = {};
    for (const [tab, fields] of Object.entries(buckets)) {
      let filled = 0, locked = 0;
      for (const [v, wasLocked] of fields) {
        if (isFilled(v)) filled += 1;
        if (wasLocked) locked += 1;
      }
      out[tab] = { filled, locked, total: fields.length };
    }
    return out;
  }, [event, crew.length]);

  // Packing progress
  const [packingProgress, setPackingProgress] = useState(null);
  const [airspaceModalOpen, setAirspaceModalOpen] = useState(false);
  useEffect(() => {
    api.get(`/events/${id}/packing`, { silent: true })
      .then(data => setPackingProgress({ filled: data.checks?.length || 0, total: data.totalItems || 0 }))
      .catch(() => {});
  }, [id]);

  // Map fullscreen — local state, not persisted (modal, not preference).
  const [mapFullscreen, setMapFullscreen] = useState(false);

  // Producer site map share — fetched in browser from studio's public API.
  // Producer sends `X-Frame-Options: deny` so we can't iframe the editor;
  // editing is handled in a new tab. To still get "edits show up when you
  // come back" without polling, we set a flag on click and refetch ONCE
  // when the tab regains focus.
  const [siteMapRefreshKey, setSiteMapRefreshKey] = useState(0);
  const editPendingRef = useRef(false);
  const siteMapShare = useSiteMapShare(event?.site_map_url, siteMapRefreshKey);
  const canEditSiteMap = !!(user?.can_producer || user?.can_admin);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && editPendingRef.current) {
        editPendingRef.current = false;
        setSiteMapRefreshKey((k) => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Esc closes fullscreen. Listener only attaches while open so we don't
  // intercept Esc elsewhere on the page.
  useEffect(() => {
    if (!mapFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMapFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapFullscreen]);

  // Lock body scroll while fullscreen so wheel events go to the map.
  useEffect(() => {
    if (!mapFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mapFullscreen]);

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete "${event.name}"?`,
      message: 'This cannot be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted');
      history.push('/events');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (changingStatus) return;
    setChangingStatus(true);
    try {
      const updated = await api.patch(`/events/${id}`, { body: { status: newStatus } });
      setEvent(updated);
      toast.success(`Status changed to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleNotifyCrew = async () => {
    if (notifying) return;
    setNotifying(true);
    try {
      const result = await api.post(`/events/${id}/notify`);
      toast.success(result.message || 'Update email sent to crew');
    } catch (error) {
      toast.error('Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  if (loading) return <LogoSpinner label="Loading event" />;
  if (!event) return null;

  // Build `locks` arrays for <SectionHeading>. Pass any number of lock keys,
  // it resolves each to { key, label, on } from the event row. Keeping this
  // per-render (not memoized) because event is tiny and this is O(n).
  const lockList = (...keys) =>
    keys.map(k => ({ key: k, label: LOCK_LABELS[k], on: !!event[k] }));

  // Our own truck leaving is still a drive, whatever the arranged external leg is,
  // so the date labels read "Leaves / Returns" the moment a vehicle is assigned.
  const eventDrivesOut = drivesOut((event.vehicles || []).length > 0, event.transport_mode);

  const buffLs = bufferedCount(event.ls_drone_count, event.buffer_percentage);
  const buffPyro = bufferedCount(event.pyro_drone_count, event.buffer_percentage);
  const totalBuffered = buffLs + buffPyro;
  const batteryCount = totalBuffered * (event.is_double_show ? 2 : 1);

  const copyConfirmation = () => {
    const d = event.details_json || {};
    const fmt12 = (t) => {
      if (!t) return '';
      const [h, m] = t.split(':').map(Number);
      const ampm = h >= 12 ? 'pm' : 'am';
      return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
    };

    const lines = [];
    lines.push(`Show Date: ${formatEventDate(event.event_date, 'MMMM D, YYYY')}`);
    lines.push(`Launch Location: ${event.launch_latitude ? `${event.launch_latitude}, ${event.launch_longitude}` : (event.location || '')}`);
    lines.push('*please review the safety map attached to ensure the entire safety perimeter will be cleared during the duration of the show');
    lines.push(`Firefly Load In: ${fmt12(d.load_in)}`);
    lines.push(`Sound Check: ${fmt12(d.sound_check)}`);
    lines.push(`Safety Perimeter Secured: *${fmt12(d.perimeter_secured)}*`);
    lines.push(`${event.is_double_show ? '1st Drone Launch Time' : 'Drone Launch Time'}: ${event.show_time ? fmt12(event.show_time) : ''}`);
    if (event.is_double_show && event.second_show_time) {
      lines.push(`2nd Drone Launch Time: ${fmt12(event.second_show_time)}`);
    }
    lines.push(`Safety Perimeter Lifted: ${fmt12(d.perimeter_lifted)}`);
    lines.push(`Firefly Load Out: ${fmt12(d.load_out)}`);
    lines.push(`Firefly Onsite Contact: ${d.onsite_contact_name || ''}${d.onsite_contact_phone ? ' // ' + d.onsite_contact_phone : ''}`);

    // Client point of contact
    const clientContact = event.contacts?.find(c => c.role?.toLowerCase().includes('client') || c.role?.toLowerCase().includes('poc'));
    const contactName = clientContact ? clientContact.name : '';
    const contactPhone = clientContact?.phone || '';
    lines.push(`*${event.name}* Point of Contact: ${contactName}${contactPhone ? ' // ' + contactPhone : ''}`);

    // Audio contact
    const audioContact = event.contacts?.find(c => c.role?.toLowerCase().includes('audio'));
    lines.push(`Audio Contact: ${audioContact ? audioContact.name : ''}${audioContact?.phone ? ' // ' + audioContact.phone : ''}`);

    lines.push(`Additional Notes: ${event.notes || ''}`);
    lines.push('');
    lines.push('Please let me know if you have any questions on the above, and we are looking forward to your show!');

    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Confirmation copied to clipboard');
  };

  const daysUntil = parseDate(event.event_date).diff(dayjs(), 'day');
  const zone = daysUntil < 0 ? { label: 'PAST', tag: { tone: 'neutral' } }
    : daysUntil < 7 ? { label: 'RED', tag: { hex: CATEGORY.red.ink.hex } }
    : daysUntil < 14 ? { label: 'YELLOW', tag: { hex: CATEGORY.yellow.ink.hex } }
    : { label: 'GREEN', tag: { hex: CATEGORY.green.ink.hex } };

  return (
    <PageLayout
      kind="record"
      thread={<MessageThread entityType="event" entityId={id} />}
      header={(
        <>
      {/* Back link */}
      <BackLink to="/events" label="Events" className="mb-5" />

      {/* ---- FULL-WIDTH HEADER BLOCK ---- */}
      <div className="bg-gray-800 border border-gray-700/60 rounded-md overflow-hidden relative mb-5">
        {/* Subtle top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />

        {/* Zone A: Identity Banner */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight truncate">
                {event.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <Tag {...(STATUS_TAG[getDisplayStatus(event)] || {})} className="capitalize">
                  {getDisplayStatus(event)}
                </Tag>
                <Tag {...zone.tag}>
                  {zone.label} {daysUntil >= 0 ? `(${daysUntil}d)` : ''}
                </Tag>
                {event.is_double_show && (
                  <Tag hex={CATEGORY.yellow.ink.hex}>Double Show</Tag>
                )}
                <EventAirspaceWidget eventId={id} onClick={() => setAirspaceModalOpen(true)} />
                {event.tour_id && tourData && (
                  <Link
                    to={`/events/tours/${event.tour_id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-500/15 text-cyan-400 ring-1 ring-inset ring-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.813a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.5 8.94" /></svg>
                    {tourData.name}
                  </Link>
                )}
                {event.monday_item_id && (user?.can_producer || user?.isadmin) && (
                  <a
                    href={`https://firefly-drone-shows-company.monday.com/boards/18314503701/pulses/${event.monday_item_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-500/15 text-orange-400 ring-1 ring-inset ring-orange-500/30 hover:bg-orange-500/25 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                    Monday
                  </a>
                )}
              </div>
            </div>

            {/* Date callout */}
            <div className="hidden sm:flex flex-col items-end flex-shrink-0">
              <span className="text-xs text-on-surface-variant uppercase tracking-wide">Event Date</span>
              <span className="text-lg font-semibold text-white mt-0.5">
                {formatEventDate(event.event_date)}
              </span>
            </div>
          </div>
        </div>

        {/* Zone B: Primary Workflow Action */}
        <Reveal show={!!canEdit && !['cancelled', 'completed'].includes(event.status)}>
          <div className="px-5 py-3 bg-gray-900/40 border-t border-gray-700/50 flex flex-wrap items-center gap-3">
            {event.status === 'pending' && (
              <button onClick={() => handleStatusChange('confirmed')}
                className={buttonClass('filled', 'md')}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                Confirm Event
              </button>
            )}
            {event.status === 'confirmed' && (
              <Link to={`/packing/${id}`}
                className={buttonClass('filled', 'md')}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>
                Pack & Ship
              </Link>
            )}
            <div className="flex-1" />
            <button onClick={() => handleStatusChange('cancelled')}
              className={buttonClass('danger-quiet', 'md')}>
              Cancel Event
            </button>
          </div>
        </Reveal>

        {/* Zone C: Utility Toolbar */}
        <div className="px-5 py-2.5 border-t border-gray-700/50 flex flex-wrap items-center gap-1">
          {event.gcal_event_id && (
            <a
              href={`https://calendar.google.com/calendar/r/month/${event.event_date.replace(/-/g, '/')}`}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClass('quiet', 'sm')}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>
              GCal
            </a>
          )}
          {canEdit && (
            <button
              disabled={syncing}
              onClick={async () => {
                if (syncing) return;
                setSyncing(true);
                try {
                  const result = await api.post(`/events/${id}/sync-gcal`);
                  setEvent(result.event);
                  toast.success('Synced to Google Calendar');
                } catch (err) {
                  toast.error(err?.message || 'GCal sync failed');
                } finally {
                  setSyncing(false);
                }
              }}
              className={buttonClass('quiet', 'sm')}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" /></svg>
              {syncing ? 'Syncing...' : 'Sync'}
            </button>
          )}
          <button
            onClick={copyConfirmation}
            className={buttonClass('quiet', 'sm')}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg>
            Copy Confirmation
          </button>
          {canEdit && (
            <button
              disabled={notifying}
              onClick={handleNotifyCrew}
              className={buttonClass('quiet', 'sm')}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
              {notifying ? 'Sending...' : 'Notify Crew'}
            </button>
          )}
          {/* Post-show debrief. A modal, not a tab: the tab strip mirrors
              EventForm and feeds the completion ring, and a debrief written after
              the show is never part of "is this show ready". Not gated on canEdit
              — anyone may read it, and the modal asks the server who may write. */}
          <button
            onClick={() => setPostShowOpen(true)}
            className={buttonClass('quiet', 'sm')}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Post Show Notes
          </button>
          {canEdit && (
            <>
              <span className="w-px h-4 bg-gray-700/60 mx-1" />
              <Link
                to={`/events/${id}/edit?tab=${activeTab}`}
                className={buttonClass('quiet', 'sm')}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                Edit
              </Link>
            </>
          )}
          <div className="flex-1" />
          {canEdit && (
            <button
              onClick={handleDelete}
              className={buttonClass('danger-quiet', 'sm')}
            >
              Delete
            </button>
          )}
        </div>
      </div>
        </>
      )}
    >

      {/* ---- TAB BAR ---- Mirrors EventForm's segmented control, rings and all,
           so the read view and the editor are one object in two modes. Ring
           counts come from tabCompletion, which reads EVENT_LOCKS directly. */}
      <Tabs
        fill
        size="lg"
        label="Event sections"
        value={activeTab}
        onChange={setActiveTab}
        tabs={DETAIL_TABS.map(([key, label]) => ({
          key,
          label,
          ring: {
            filled: tabCompletion[key]?.filled || 0,
            locked: tabCompletion[key]?.locked || 0,
            total: tabCompletion[key]?.total || 1,
          },
        }))}
      />

      <FadeIn key={activeTab} className="space-y-5">

      {/* ---- NOTES (main) ---- Always rendered, like the editor's textarea. An
           empty notes box is information: it says nobody has written anything,
           which the old conditional card hid by simply not existing. */}
      {activeTab === 'main' && (
        <Card>
          <SectionHeading>Notes</SectionHeading>
          <p className="text-sm text-on-surface whitespace-pre-wrap leading-relaxed">
            {event.notes || <span className="text-on-surface-disabled">--</span>}
          </p>
        </Card>
      )}

      {/* ---- TOUR INFO (if in a tour) ---- */}
      {tourData && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <SectionHeading>Tour</SectionHeading>
            <Link
              to={`/events/tours/${tourData.id}`}
              className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              View Tour Details →
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-lg font-semibold text-white">{tourData.name}</span>
            <span className="text-xs text-on-surface-variant">{tourData.events?.length || 0} shows</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {(tourData.events || [])
              .sort((a, b) => (parseDate(a.event_date)?.valueOf() || 0) - (parseDate(b.event_date)?.valueOf() || 0))
              .map(ev => (
                <Link
                  key={ev.id}
                  to={`/events/${ev.id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset transition-colors ${
                    String(ev.id) === id
                      ? 'bg-primary/15 text-primary ring-primary/30'
                      : 'bg-gray-700/40 text-on-surface ring-[rgb(70_70_70/0.40)] hover:bg-gray-700/60'
                  }`}
                >
                  {ev.name}
                  <span className="text-on-surface-variant">{formatEventDate(ev.event_date, 'M/D')}</span>
                </Link>
              ))}
          </div>
          <div className="grid grid-cols-3 gap-4 bg-gray-900/50 rounded-md p-3 border border-gray-700/40">
            <div>
              <div className="text-[10px] text-on-surface-variant uppercase tracking-wide">Fleet Needed</div>
              <div className="text-sm font-bold text-white">{tourData.committed_total}</div>
              {tourData.committed_pyro > 0 && (
                <div className="text-[10px] text-orange-400/70">{tourData.committed_pyro} pyro</div>
              )}
            </div>
            <div>
              <div className="text-[10px] text-on-surface-variant uppercase tracking-wide">Batteries</div>
              <div className="text-sm font-bold text-white">{tourData.committed_batteries}</div>
            </div>
            <div>
              <div className="text-[10px] text-on-surface-variant uppercase tracking-wide">Transit</div>
              <div className="text-sm font-bold text-white">
                {tourData.window_start && tourData.window_end
                  ? `${parseDate(tourData.window_end).diff(parseDate(tourData.window_start), 'day')}d`
                  : '--'}
              </div>
              <div className="text-[10px] text-on-surface-variant">
                {tourData.window_start && formatEventDate(tourData.window_start, 'M/D')} → {tourData.window_end && formatEventDate(tourData.window_end, 'M/D')}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ---- DETAILS GRID (main) ---- */}
      {activeTab === 'main' && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Event Details */}
        <Card>
          <SectionHeading locks={lockList('locked_main')}>Event Details</SectionHeading>
          {/* Field order and the two-up date/time pairing mirror EventForm's Main
              tab. Event Name is deliberately omitted: the header banner already
              carries it in 24pt, and repeating it here would be the one field
              nobody needs a box for.
              Location, show height and surface live on the Location tab now —
              EVENT_LOCKS assigns them to locked_location, so leaving them here
              made that tab's completion ring count fields it did not show. */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Event Date">{formatEventDate(event.event_date)}</Field>
              <Field
                label={event.is_double_show ? '1st Show Time' : 'Show Time'}
                hint={event.show_time && event.timezone ? `(${getTzAbbrev(event.timezone, event.event_date)})` : null}
              >
                {event.show_time}
              </Field>
            </div>
            {(event.end_date || event.rain_date) && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="End Date">{event.end_date ? formatEventDate(event.end_date) : null}</Field>
                <Field label="Rain Date">{event.rain_date ? formatEventDate(event.rain_date) : null}</Field>
              </div>
            )}
            {event.is_double_show && (
              <Field
                label="2nd Show Time"
                hint={event.second_show_time && event.timezone ? `(${getTzAbbrev(event.timezone, event.event_date)})` : null}
              >
                {event.second_show_time}
              </Field>
            )}
            {/* Preview links moved to the Creative tab, where EVENT_LOCKS files
                them and where the editor puts them. They were counted in the
                Creative ring while only ever rendering here, so the tab showed
                "no creative details yet" for an event that had a preview. */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Created By">{event.creator_firstname}</Field>
            </div>
          </div>
        </Card>

        {/* Drone Allocation */}
        <Card>
          <SectionHeading>Drone Allocation</SectionHeading>
          {/* Three-up to match EventForm, which puts the two counts and the buffer
              on one row. The buffered figure rides inside each count's box rather
              than getting its own field: it is a derived value, and giving it equal
              billing to an entered number is how people start trying to edit it. */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Standard">
              <span className="tabular-nums">{event.ls_drone_count}</span>
              <span className="text-on-surface-disabled">&rarr;</span>
              <span className="text-blue-400 font-medium tabular-nums">{buffLs}</span>
            </Field>
            <Field label="Pyro">
              <span className="tabular-nums">{event.pyro_drone_count}</span>
              <span className="text-on-surface-disabled">&rarr;</span>
              <span className="text-blue-400 font-medium tabular-nums">{buffPyro}</span>
            </Field>
            <Field label="Buffer">
              <span className="tabular-nums">{event.buffer_percentage}%</span>
            </Field>
          </div>

          {/* Highlighted totals */}
          <div className="mt-3 bg-gray-900/50 rounded-md p-3 border border-gray-700/40 flex gap-6">
            <div>
              <div className="text-[11px] text-on-surface-variant uppercase tracking-wide">Total Drones</div>
              <div className="text-2xl font-bold text-white leading-tight">{totalBuffered}</div>
            </div>
            <div className="border-l border-gray-700/50 pl-6">
              <div className="text-[11px] text-on-surface-variant uppercase tracking-wide">Batteries</div>
              <div className="text-2xl font-bold text-white leading-tight">{batteryCount}</div>
            </div>
            {event.is_double_show && (
              <div className="ml-auto self-center">
                <span className="text-xs text-yellow-400/80 bg-yellow-500/10 px-2 py-1 rounded">x2 show</span>
              </div>
            )}
          </div>
        </Card>
      </div>
      )}

      {/* ---- RUN OF SHOW (main) ---- */}
      {activeTab === 'main' && (() => {
        const d = event.details_json || {};
        const slots = [
          { label: 'Load In', value: d.load_in },
          { label: 'Sound Check', value: d.sound_check },
          { label: 'Four Corners', value: d.four_corners },
          { label: 'Perimeter Secured', value: d.perimeter_secured },
          { label: 'Perimeter Lifted', value: d.perimeter_lifted },
          { label: 'Load Out', value: d.load_out },
        ].filter(s => s.value);
        if (slots.length === 0) return null;
        return (
          <div className="bg-gray-800 border border-gray-700/60 rounded-md px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="text-[10px] text-on-surface-variant uppercase tracking-widest font-semibold">Run of Show</span>
            {slots.map(s => (
              <span key={s.label} className="inline-flex items-baseline gap-1.5">
                <span className="text-[10px] text-on-surface-variant">{s.label}</span>
                <span className="text-xs font-semibold text-on-surface">{s.value}</span>
              </span>
            ))}
          </div>
        );
      })()}

      {/* ---- CREATIVE (creative) ---- */}
      {activeTab === 'creative' && <SparkCreativePanel eventId={id} />}
      {activeTab === 'creative' && (() => {
        const d = event.details_json || {};
        const previewLinks = Array.isArray(event.preview_links) ? event.preview_links : [];
        const hasCreative = previewLinks.length > 0 || d.music || d.time_machine || d.music_link_code || d.creative;
        if (!hasCreative) {
          return (
            <EmptyTab editTo={`/events/${id}/edit?tab=${activeTab}`} canEdit={canEdit}>
              No creative details yet. Preview links, music, Time Machine and the show write-up live here.
            </EmptyTab>
          );
        }
        return (
          <Card>
            <SectionHeading locks={lockList('locked_creative')}>Creative</SectionHeading>
            {previewLinks.length > 0 && (
              <div className="mb-4 pb-4 border-b border-gray-700/40">
                <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-2">
                  {previewLinks.length > 1 ? 'Preview Links' : 'Preview'}
                </label>
                {/* One clickable row per link. A double show has a render per
                    show; unlabeled entries fall back to their position, the
                    same fallback the editor shows as placeholder text. */}
                <div className="flex flex-wrap gap-2">
                  {previewLinks.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                      className={buttonClass('quiet', 'sm')}>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-4.5-6h6m0 0v6m0-6L9.75 14.25" /></svg>
                      {link.label || (previewLinks.length > 1 ? `Show ${i + 1}` : 'View Preview')}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Music">{d.music}</Field>
                <Field label="Time Machine">{d.time_machine}</Field>
              </div>
              <Field label="Music Link Code">{d.music_link_code}</Field>
            </div>
            {d.creative && (
              <div className="mt-4 pt-4 border-t border-gray-700/40">
                <label className="block text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold mb-1">Creative</label>
                {/* Kept as innerHTML to match the existing behavior: the field is
                    authored prose that may already carry markup. Newlines are
                    converted the same way they always were. */}
                <p className="text-sm text-on-surface leading-relaxed" dangerouslySetInnerHTML={{ __html: d.creative.replace(/\n/g, '<br>') }} />
              </div>
            )}
          </Card>
        );
      })()}

      {/* ---- TRANSPORT & SHIPPING (logistics) ---- */}
      {activeTab === 'logistics' && (
      <Card>
        <SectionHeading locks={lockList('locked_logistics_transport')}>
          Transport & Shipping
        </SectionHeading>
        {/* Ship dates keep their derived "Nd before / Nd after" offsets, and the
            Packing ring stays below a rule. Neither exists on the edit page: the
            offsets are computed and packing progress comes from another table, so
            they are display-only information this page must not lose. */}
        <div className="space-y-4">
          {/* Two independent facts, not one either/or: an assigned truck means we
              drive, and transport_mode describes whatever leg somebody else
              carries. A show can be both. See components/TransportChips.tsx. */}
          <Field label="Transport">
            <TransportChips
              hasVehicle={(event.vehicles || []).length > 0}
              transportArranged={!!event.transport_arranged}
              transportMode={event.transport_mode}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={eventDrivesOut ? 'Leaves' : 'Ships Out'}>
              {event.ship_out_date ? (
                <>
                  {formatEventDate(event.ship_out_date)}
                  <span className="text-on-surface-variant text-xs">({parseDate(event.event_date).diff(parseDate(event.ship_out_date), 'day')}d before)</span>
                </>
              ) : null}
            </Field>
            <Field label={eventDrivesOut ? 'Returns' : 'Ships Back'}>
              {event.ship_back_date ? (
                <>
                  {formatEventDate(event.ship_back_date)}
                  <span className="text-on-surface-variant text-xs">({parseDate(event.ship_back_date).diff(parseDate(event.end_date || event.event_date), 'day')}d after)</span>
                </>
              ) : null}
            </Field>
          </div>

          {/* Tracking numbers are shipping-only, exactly as EVENT_LOCKS gates them
              (conditional on transport_mode !== 'driving'). */}
          {event.transport_mode !== 'driving' && (
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Tracking # Out', event.tracking_number_out],
                ['Tracking # Return', event.tracking_number_return],
              ].map(([label, num]) => (
                <Field key={label} label={label}>
                  {num ? (
                    <a href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(num)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline underline-offset-2 inline-flex items-center gap-1">
                      {num}
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                    </a>
                  ) : null}
                </Field>
              ))}
            </div>
          )}

          <Reveal show={!!(packingProgress && packingProgress.filled > 0)}>
            {packingProgress && (
            <div className="pt-4 border-t border-gray-700/40">
              <Field label="Packing">
                <Link to={`/packing/${id}`} className="inline-flex items-center gap-2 hover:text-primary transition-colors">
                  <CompletionRing filled={packingProgress.filled} total={packingProgress.total} size={16} />
                  <span className={`text-sm tabular-nums ${packingProgress.filled >= packingProgress.total ? 'text-green-400' : 'text-on-surface'}`}>
                    {packingProgress.filled}/{packingProgress.total}
                  </span>
                  <Appear show={packingProgress.filled >= packingProgress.total} className="text-[10px] text-green-400 font-semibold uppercase tracking-wider">Ready</Appear>
                </Link>
              </Field>
            </div>
            )}
          </Reveal>
        </div>
      </Card>
      )}

      {/* ---- CREW & LOGISTICS (logistics) ---- */}
      {activeTab === 'logistics' && (() => {
        const d = event.details_json || {};
        const activeLogistics = LOGISTICS_FIELDS.filter(f => typeof d[f.key] === 'string' && d[f.key].trim());
        const hasCrew = crew.length > 0;
        const hasExternal = externalCrew.length > 0;
        const hasLogistics = activeLogistics.length > 0;
        if (!hasCrew && !hasExternal && !hasLogistics) return null;
        // Combined render of internal + external rows so they share one divide-y
        const allCrewRows = [
          ...crew.map(m => ({ kind: 'internal', key: m.user_id, member: m })),
          ...externalCrew.map(m => ({ kind: 'external', key: `ext-${m.id}`, member: m })),
        ];
        return (
          <Card>
            {(hasCrew || hasExternal) && (
              <>
                <SectionHeading locks={lockList('locked_logistics_crew')}>Crew</SectionHeading>
                <div className={`divide-y divide-gray-700/40${hasLogistics ? ' mb-5' : ''}`}>
                  {allCrewRows.map((row, i) => (
                    <div key={row.key || i} className={`flex items-start gap-3 ${i > 0 ? 'pt-3' : ''} ${i < allCrewRows.length - 1 ? 'pb-3' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {row.kind === 'internal' ? (
                            <>
                              <span className="text-sm font-medium text-white">{row.member.firstname} {row.member.lastname}</span>
                              {row.member.role && (
                                <Tag {...(ROLE_TAG[row.member.role.toLowerCase()] || ROLE_TAG.default)} size="sm" className="uppercase">
                                  {row.member.role}
                                </Tag>
                              )}
                              {/* Day coverage, shown only when it differs from "the
                                  whole show" — see crewCoverage. This is the field
                                  that keeps this page honest against /schedule. */}
                              {(() => {
                                const cov = crewCoverage(row.member, event);
                                if (!cov) return null;
                                return (
                                  <Tag {...COVERAGE_TAG[cov.tone]} size="sm" className="tabular-nums">
                                    {cov.text}
                                  </Tag>
                                );
                              })()}
                            </>
                          ) : (
                            <>
                              <span className="text-sm font-medium text-white">{row.member.name}</span>
                              <Tag hex={CATEGORY.orange.ink.hex} size="sm">External</Tag>
                              {/* Email + phone shown small for quick contact */}
                              <span className="text-[11px] text-on-surface-variant">
                                <a href={`mailto:${row.member.email}`} className="hover:text-on-surface">{row.member.email}</a>
                                {row.member.phone && (
                                  <>
                                    {' · '}
                                    <a href={`tel:${row.member.phone}`} className="hover:text-on-surface">{row.member.phone}</a>
                                  </>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                        {row.member.notes && (
                          <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">{row.member.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {hasLogistics && (
              <>
                <SectionHeading>Logistics</SectionHeading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0">
                  {activeLogistics.map((field, i) => {
                    // Map each LOGISTICS_FIELDS key to its lock column.
                    // gear_transportation is bundled under the transport lock
                    // (see event-locks.js: locked_logistics_transport).
                    const lockKeyByField = {
                      gear_transportation: 'locked_logistics_transport',
                      flight: 'locked_logistics_flights',
                      rental_car: 'locked_logistics_rental_car',
                      box_truck: 'locked_logistics_box_truck',
                      hotel: 'locked_logistics_hotel',
                      packing: 'locked_logistics_packing',
                    };
                    const lockKey = lockKeyByField[field.key];
                    return (
                      <div key={field.key} className={`py-3 ${i > 0 ? 'border-t border-gray-700/40' : ''} ${i === 1 ? 'sm:border-t-0' : ''}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-on-surface-variant">{field.icon}</span>
                          <span className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold flex-1">{field.label}</span>
                          {lockKey && (
                            <LockBadge locked={!!event[lockKey]} label={LOCK_LABELS[lockKey]} />
                          )}
                        </div>
                        <p className="text-sm text-on-surface leading-relaxed pl-6" dangerouslySetInnerHTML={{ __html: d[field.key].replace(/\n/g, '<br>') }} />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        );
      })()}

      {/* ---- CONTACTS + COORDINATES ---- Two tabs share one grid wrapper: the
           contact cards belong to Contacts, the map card to Location. The tab
           checks sit on the cards so each tab keeps the same two-up layout. */}
      {(activeTab === 'location' || ((activeTab === 'contacts') && ((event.contacts && event.contacts.length > 0) || event.details_json?.onsite_contact_name || event.details_json?.onsite_contact_phone))) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Site — address, height and surface. Always present on the Location
              tab, because "no address yet" is itself worth seeing. */}
          {activeTab === 'location' && (
            <Card>
              <SectionHeading locks={lockList('locked_location')}>Site</SectionHeading>
              <div className="space-y-4">
                <Field label="Location">
                  {event.location ? (
                    <a
                      href={event.launch_latitude
                        ? `https://www.google.com/maps?q=${event.launch_latitude},${event.launch_longitude}`
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors duration-150 hover:underline inline-flex items-center gap-1"
                    >
                      {formatAddress(event.location)}
                      <svg className="w-3 h-3 opacity-50 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                    </a>
                  ) : null}
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Show Height">{event.show_height_ft ? `${event.show_height_ft} ft` : null}</Field>
                  <Field label="Surface">
                    {event.surface_type ? <span className="capitalize">{event.surface_type}</span> : null}
                  </Field>
                </div>
              </div>
            </Card>
          )}

          {/* Firefly Onsite Contact */}
          {activeTab === 'contacts' && (event.details_json?.onsite_contact_name || event.details_json?.onsite_contact_phone) && (
            <Card>
              <SectionHeading locks={lockList('locked_contacts')}>
                Firefly Onsite Contact
              </SectionHeading>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name">
                  {event.details_json?.onsite_contact_name
                    ? <span className="text-white font-medium">{event.details_json.onsite_contact_name}</span>
                    : null}
                </Field>
                <Field label="Phone">
                  {event.details_json?.onsite_contact_phone ? (
                    <a href={`tel:${event.details_json.onsite_contact_phone}`} className="text-blue-400/80 hover:text-blue-300 transition-colors duration-150 inline-flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                      {event.details_json.onsite_contact_phone}
                    </a>
                  ) : null}
                </Field>
              </div>
            </Card>
          )}

          {/* Contacts */}
          {activeTab === 'contacts' && event.contacts && event.contacts.length > 0 && (
            <Card>
              <SectionHeading locks={lockList('locked_contacts')}>Contacts</SectionHeading>
              <div className="space-y-0 divide-y divide-gray-700/40">
                {event.contacts.map((c, i) => (
                  <div key={i} className={`text-sm ${i > 0 ? 'pt-3' : ''} ${i < event.contacts.length - 1 ? 'pb-3' : ''}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{c.name}</span>
                      {c.role && (
                        <Tag tone="neutral" size="sm">{c.role}</Tag>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="text-blue-400/80 hover:text-blue-300 transition-colors duration-150 text-xs inline-flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
                          {c.phone}
                        </a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} className="text-blue-400/80 hover:text-blue-300 transition-colors duration-150 text-xs inline-flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                          {c.email}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Coordinates */}
          {activeTab === 'location' && event.launch_latitude && (
            <Card>
              <SectionHeading locks={lockList('locked_location')}>Coordinates</SectionHeading>
              {/* Crowd Distance is COMPUTED here (haversine over the two pins) and
                  exists nowhere on the edit page, so it survives the restyle with
                  its own rule above it. Same for the Site Map row, which reflects
                  studio share state rather than a stored field. */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Launch Site">
                    <a
                      href={`https://www.google.com/maps?q=${event.launch_latitude},${event.launch_longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400/80 hover:text-blue-300 transition-colors duration-150 font-mono text-xs inline-flex items-center gap-1"
                    >
                      {event.launch_latitude?.toFixed(5)}, {event.launch_longitude?.toFixed(5)}
                      <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                    </a>
                  </Field>
                  <Field label="Audience">
                    {event.audience_latitude ? (
                      <a
                        href={`https://www.google.com/maps?q=${event.audience_latitude},${event.audience_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400/80 hover:text-blue-300 transition-colors duration-150 font-mono text-xs inline-flex items-center gap-1"
                      >
                        {event.audience_latitude?.toFixed(5)}, {event.audience_longitude?.toFixed(5)}
                        <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
                      </a>
                    ) : null}
                  </Field>
                </div>
                {event.launch_latitude && event.audience_latitude && (() => {
                  const R = 20902231;
                  const dLat = (event.audience_latitude - event.launch_latitude) * Math.PI / 180;
                  const dLng = (event.audience_longitude - event.launch_longitude) * Math.PI / 180;
                  const a = Math.sin(dLat/2)**2 + Math.cos(event.launch_latitude * Math.PI/180) * Math.cos(event.audience_latitude * Math.PI/180) * Math.sin(dLng/2)**2;
                  const dist = Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
                  return (
                    <div className="pt-4 border-t border-gray-700/40">
                      <Field label="Crowd Distance">
                        <span className="font-bold text-white">~{dist.toLocaleString()} ft</span>
                      </Field>
                    </div>
                  );
                })()}
                {/* Producer site map — view + edit affordances inline so the
                    map area stays uncluttered (no overlay pencil). Edit is
                    capability-gated; it sets editPendingRef so we refetch
                    once when the user comes back from producer. */}
                {siteMapShare.state !== 'idle' && siteMapShare.state !== 'invalid' && (
                  <Field label="Site Map">
                    <SiteMapDataValue
                      share={siteMapShare}
                      canEdit={canEditSiteMap}
                      onEditClick={() => { editPendingRef.current = true; }}
                    />
                  </Field>
                )}
              </div>

              {/* Map — normal (inline) or fullscreen (portaled to <body>).
                  Payload is built once as `mapInner` below so the two stages
                  can't drift. scrollWheelZoom is enabled in fullscreen only,
                  matching the list-page behavior (inline should yield page
                  scroll). */}
              {(() => {
                const mapInner = (
                  <MapContainer
                    center={[event.launch_latitude, event.launch_longitude]}
                    zoom={event.audience_latitude ? 15 : 14}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={mapFullscreen}
                    attributionControl={false}
                    key={mapFullscreen ? 'fs' : 'inline'}
                  >
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                    <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
                    {/* FitBounds priority: studio site map bounds (when ready) → launch+audience → launch alone (handled by MapContainer center). */}
                    {siteMapShare.state === 'ready' && siteMapShare.bounds ? (
                      <FitBounds bounds={[
                        [siteMapShare.bounds.sw[1], siteMapShare.bounds.sw[0]],
                        [siteMapShare.bounds.ne[1], siteMapShare.bounds.ne[0]],
                      ]} />
                    ) : event.audience_latitude && (
                      <FitBounds bounds={[
                        [event.launch_latitude, event.launch_longitude],
                        [event.audience_latitude, event.audience_longitude],
                      ]} />
                    )}
                    <SiteMapLayer share={siteMapShare} />
                    <Marker position={[event.launch_latitude, event.launch_longitude]} icon={launchIcon}>
                      <Popup><span className="text-xs font-medium">Launch Site</span></Popup>
                    </Marker>
                    {event.audience_latitude && (
                      <Marker position={[event.audience_latitude, event.audience_longitude]} icon={audienceIcon}>
                        <Popup><span className="text-xs font-medium">Audience</span></Popup>
                      </Marker>
                    )}
                  </MapContainer>
                );

                const expandBtn = (
                  <Tooltip label="Fullscreen">
                  <button
                    onClick={() => setMapFullscreen(true)}
                    aria-label="Fullscreen map"
                    className="absolute top-2 right-2 z-[500] p-1.5 rounded bg-gray-800/95 border border-gray-700/60 backdrop-blur-sm text-on-surface hover:text-white hover:bg-gray-700 shadow-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4h4M20 8V4h-4M4 16v4h4M20 16v4h-4" />
                    </svg>
                  </button>
                  </Tooltip>
                );

                const closeBtn = (
                  <Tooltip label="Exit fullscreen (Esc)">
                  <button
                    onClick={() => setMapFullscreen(false)}
                    aria-label="Exit fullscreen"
                    className="absolute top-3 right-3 z-[500] p-1.5 rounded bg-gray-800/95 border border-gray-700/60 backdrop-blur-sm text-on-surface hover:text-white hover:bg-gray-700 shadow-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  </Tooltip>
                );

                return (
                  <>
                    <div className="mt-4 rounded-md overflow-hidden border border-gray-700/40 relative z-0" style={{ height: 220 }}>
                      {!mapFullscreen && mapInner}
                      {!mapFullscreen && expandBtn}
                    </div>
                    {mapFullscreen && createPortal(
                      <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] bg-gray-900">
                        {mapInner}
                        {closeBtn}
                        <div className="absolute bottom-4 right-4 z-[500] px-2 py-1 bg-gray-800/80 border border-gray-700/60 rounded text-[10px] text-on-surface-variant backdrop-blur-sm">
                          Press <kbd className="px-1 py-0.5 bg-gray-900/80 border border-gray-700 rounded text-on-surface text-[9px]">Esc</kbd> to close
                        </div>
                      </div>,
                      document.body
                    )}
                  </>
                );
              })()}
              <div className="flex gap-4 mt-2 text-[11px] text-on-surface-variant">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />Launch</span>
                {event.audience_latitude && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block" />Audience</span>}
              </div>
            </Card>
          )}

        </div>
      )}

      {/* Contacts tab with nothing in it yet. Location can't be empty (the Site
          card always renders), so this is the only other empty case. */}
      {activeTab === 'contacts'
        && !(event.contacts && event.contacts.length > 0)
        && !event.details_json?.onsite_contact_name
        && !event.details_json?.onsite_contact_phone && (
        <EmptyTab editTo={`/events/${id}/edit?tab=${activeTab}`} canEdit={canEdit}>
          No contacts yet. Add the client contact and whoever from Firefly is onsite.
        </EmptyTab>
      )}

      </FadeIn>

      {postShowOpen && (
        <PostShowNotesModal
          eventId={id}
          eventName={event.name}
          onClose={() => setPostShowOpen(false)}
        />
      )}

      {airspaceModalOpen && (
        <EventAirspaceModal
          eventId={id}
          onClose={() => setAirspaceModalOpen(false)}
        />
      )}
    </PageLayout>
  );
};

export default EventDetail;
